import React from "react";
import { Sparkles, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInsights } from "../../data/insightsStore";

const tone = {
  high: { dot: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200", label: "Critical" },
  med:  { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Watch" },
  low:  { dot: "bg-indigo-500", chip: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Insight" },
};

export const AIRiskAlerts = () => {
  const navigate = useNavigate();
  const { data } = useInsights();
  const alerts = data?.alerts || [];
  const dealCount = data?.deal_count || 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white cf-shadow overflow-hidden" data-testid="ai-risk-alerts">
      <div className="relative p-5 border-b border-slate-100 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-900/30 text-white">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 cf-blob" />
        </div>
        <div className="relative flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center">
            <Sparkles size={17} className="text-indigo-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-indigo-300 font-bold">ChainFlow Risk Engine</p>
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400">live · {dealCount} deals</span>
            </div>
            <h2 className="font-display text-[20px] font-bold mt-0.5">{alerts.length} active risk insight{alerts.length === 1 ? "" : "s"}</h2>
            <p className="text-[12.5px] text-slate-300 mt-0.5">
              Multi-signal pattern detection across solicitor SLAs, mortgage cadence, document compliance and chain stability.
            </p>
          </div>
        </div>
      </div>
      {alerts.length === 0 ? (
        <div className="px-5 py-8 text-center" data-testid="risk-alerts-empty">
          <p className="text-[13px] text-slate-500">All transactions running clean — no active risk signals.</p>
        </div>
      ) : (
        <ul>
          {alerts.map((a) => {
            const t = tone[a.tone] || tone.low;
            return (
              <li
                key={a.id}
                data-testid={`risk-alert-${a.id}`}
                className="px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors flex items-start gap-3"
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full ${t.dot} ${a.tone === "high" ? "cf-pulse" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-[13.5px] font-semibold text-slate-900">{a.title}</p>
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${t.chip}`}>{t.label}</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">{a.category}</span>
                  </div>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed">{a.detail}</p>
                  <p className="text-[11px] text-slate-500 mt-1.5"><span className="text-slate-400">Modelled impact: </span><b className="text-slate-900">{a.impact}</b></p>
                </div>
                <button
                  data-testid={`risk-alert-action-${a.id}`}
                  onClick={() => a.deal_id && navigate(`/deal-rooms/${a.deal_id}`)}
                  className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-semibold transition-colors"
                >
                  {a.action} <ChevronRight size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
